/**
 * Section Colors Utility
 * 
 * This file provides a centralized way to manage section colors based on
 * section IDs rather than hardcoded section names. This approach ensures
 * consistent colors across all environments regardless of section names.
 * 
 * IMPORTANT: This file MUST match the colors defined in the backend's sectionColors.js
 * to ensure consistency between frontend and backend rendering.
 */

// Database IDs for each section - update these with your actual MongoDB IDs
// These IDs will ensure consistency across environments
const SECTION_IDS = {
  FUNCTIONAL_ANALYSIS: '68096c7f0ed65d2770008725',
  DESIGN: '68096c7f0ed65d2770008727',
  CODING_DEVELOPMENT: '68096c7f0ed65d2770008729', 
  TESTING: '6812086a4afcec422266f72c',
  INFRASTRUCTURE: '6812086a4afcec422266f72e',
  
  // Build phase section IDs (placeholder IDs - replace with actual MongoDB IDs when available)
  BUILD_SETUP: '7001000000000000000setup',
  BUILD_CORE: '7002000000000000000core',
  BUILD_FEATURES: '7003000000000000000feat',
  BUILD_TESTING: '7004000000000000000test',
  BUILD_DEPLOYMENT: '7005000000000000000depl'
};

// Background colors for section cards
export const getSectionBackgroundColor = (sectionId) => {
  // Map section IDs to background colors
  const backgroundColors = {
    // Functional Analysis (id: 1)
    [SECTION_IDS.FUNCTIONAL_ANALYSIS]: '#e8f5e9', // Light green
    'category-0': '#e8f5e9', 
    'functional-analysis': '#e8f5e9',
    
    // Design (id: 2)
    [SECTION_IDS.DESIGN]: '#e3f2fd', // Light blue
    'category-1': '#e3f2fd',
    'design': '#e3f2fd',
    
    // Technical (id: 3) - previously "Coding & development"
    [SECTION_IDS.CODING_DEVELOPMENT]: '#fff3e0', // Light orange
    'category-2': '#fff3e0',
    'coding-development': '#fff3e0',
    'technical': '#fff3e0', // Keep for backward compatibility
    
    // Testing (id: 4) - new section
    [SECTION_IDS.TESTING]: '#f3e5f5', // Light purple
    'category-3': '#f3e5f5',
    'testing': '#f3e5f5',
    
    // Infrastructure (id: 5) - new section
    [SECTION_IDS.INFRASTRUCTURE]: '#e0f7fa', // Light cyan
    'category-4': '#e0f7fa',
    'infrastructure': '#e0f7fa',
    
    // Build Phases
    // Setup Phase
    [SECTION_IDS.BUILD_SETUP]: '#e8eaf6', // Light indigo
    'build-setup': '#e8eaf6',
    
    // Core Components Phase
    [SECTION_IDS.BUILD_CORE]: '#fce4ec', // Light pink
    'build-core': '#fce4ec',
    
    // Features Implementation Phase
    [SECTION_IDS.BUILD_FEATURES]: '#f1f8e9', // Light lime
    'build-features': '#f1f8e9',
    
    // Testing Phase
    [SECTION_IDS.BUILD_TESTING]: '#fffde7', // Light yellow
    'build-testing': '#fffde7',
    
    // Deployment Phase
    [SECTION_IDS.BUILD_DEPLOYMENT]: '#e0f2f1', // Light teal
    'build-deployment': '#e0f2f1',
    
    // Default color for any unrecognized section
    'default': '#f5f5f5' // Light gray
  };
  
  // Fallback to section name for backward compatibility
  return backgroundColors[sectionId] || backgroundColors.default;
};

// Icon colors for section cards
export const getSectionIconColor = (sectionId) => {
  // Map section IDs to icon colors
  const iconColors = {
    // Functional Analysis (id: 1)
    [SECTION_IDS.FUNCTIONAL_ANALYSIS]: '#66bb6a', // Green
    'category-0': '#66bb6a',
    'functional-analysis': '#66bb6a',
    
    // Design (id: 2)
    [SECTION_IDS.DESIGN]: '#42a5f5', // Blue
    'category-1': '#42a5f5',
    'design': '#42a5f5',
    
    // Technical (id: 3) - previously "Coding & development"
    [SECTION_IDS.CODING_DEVELOPMENT]: '#ff9800', // Orange
    'category-2': '#ff9800',
    'coding-development': '#ff9800',
    'technical': '#ff9800', // Keep for backward compatibility
    
    // Testing (id: 4) - new section
    [SECTION_IDS.TESTING]: '#ab47bc', // Purple
    'category-3': '#ab47bc',
    'testing': '#ab47bc',
    
    // Infrastructure (id: 5) - new section
    [SECTION_IDS.INFRASTRUCTURE]: '#26c6da', // Cyan
    'category-4': '#26c6da',
    'infrastructure': '#26c6da',
    
    // Build Phases
    // Setup Phase
    [SECTION_IDS.BUILD_SETUP]: '#3f51b5', // Indigo
    'build-setup': '#3f51b5',
    
    // Core Components Phase
    [SECTION_IDS.BUILD_CORE]: '#ec407a', // Pink
    'build-core': '#ec407a',
    
    // Features Implementation Phase
    [SECTION_IDS.BUILD_FEATURES]: '#8bc34a', // Lime
    'build-features': '#8bc34a',
    
    // Testing Phase
    [SECTION_IDS.BUILD_TESTING]: '#fdd835', // Yellow
    'build-testing': '#fdd835',
    
    // Deployment Phase
    [SECTION_IDS.BUILD_DEPLOYMENT]: '#009688', // Teal
    'build-deployment': '#009688',
    
    // Default color for any unrecognized section
    'default': '#475969' // Slate
  };
  
  // Selected item color
  if (sectionId === 'selected') {
    return '#f4c443'; // Yellow
  }
  
  // Fallback to default color if not found
  return iconColors[sectionId] || iconColors.default;
};

// Normalize section ID for color mapping
// This handles different formats of section ID that might come from different sources
export const normalizeSectionId = (section) => {
  if (!section) return 'default';
  
  // If section is an object with _id
  if (section._id) {
    // For database IDs like MongoDB ObjectId
    if (typeof section._id === 'string') {
      // For exact MongoDB ObjectId matching
      if (Object.values(SECTION_IDS).includes(section._id.toString())) {
        return section._id.toString();
      }
      // Try to extract a simpler ID if it's a complex database ID
      const simpleId = section._id.replace(/^[a-f0-9]+\-/, '');
      return simpleId || section._id;
    }
    return section._id;
  }
  
  // If section is an object with id
  if (section.id) {
    return section.id;
  }
  
  // If section is an object with name, convert name to kebab case
  if (section.name) {
    return section.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }
  
  // If section is a string, assume it's already an ID
  if (typeof section === 'string') {
    return section;
  }
  
  return 'default';
};

// Normalize section name for display
// Fixes inconsistent casing from database (e.g., "Ui/ux" -> "UI/UX")
export const normalizeSectionName = (sectionName) => {
  if (!sectionName) return '';

  // Check case-insensitive for known terms that need special formatting
  const lowerName = sectionName.toLowerCase().trim();

  // UI/UX variations - check if it contains ui/ux pattern
  if (lowerName === 'ui/ux' || lowerName === 'uiux' || lowerName === 'ui ux' || lowerName.includes('ui/ux')) {
    return 'UI/UX';
  }

  // API variations
  if (lowerName === 'api') {
    return 'API';
  }

  // CI/CD variations
  if (lowerName === 'ci/cd' || lowerName === 'cicd' || lowerName === 'ci cd') {
    return 'CI/CD';
  }

  // PRD variations
  if (lowerName === 'prd') {
    return 'PRD';
  }

  return sectionName;
};

// Get section description for fallback
export const getSectionDescription = (sectionName) => {
  const sectionDescriptions = {
    // Main sections
    'Functional analysis': 'Creation of Project Info, Features, User Stories etc...',
    'Design': 'Creation of Wireframes, Design Guidelines etc...',
    'Technical': 'Creation of Architecture, Technology Stack, Development Guidelines etc...',
    'Testing': 'Test cases, automation testing, security testing, performance testing etc...',
    'Infrastructure': 'Version control, CI/CD pipeline, deployment etc...',
    'Security & API': 'Define Security Requirements and API Standards',
    'Features': 'Define Core Features and User Stories',
    'Core': 'Essential Project Information and Settings',
    
    // Build phases
    'Build Setup': 'Environment setup, project configuration, dependency installation',
    'Build Core': 'Core components development, foundation classes, utilities',
    'Build Features': 'Feature implementation, business logic, components',
    'Build Testing': 'Unit tests, integration tests, end-to-end tests',
    'Build Deployment': 'Deployment configuration, CI/CD, documentation'
  };
  
  return sectionDescriptions[sectionName] || '';
};